import React, { useCallback, useMemo } from 'react';

import { useAppSelector } from '../../../hooks/react-redux';
import { Agency, Document, Globe, Mail, Opinion, PlaybookIcon, Robot, Tick, User } from '../../shared/icons';
import styles from './Roles.module.scss';
import { FeaturePermission, getAvailableFeaturePermissions } from '../users/store';
import { Scrollable } from '../../shared/scrollable/Scrollable';
import { Icon } from '../../shared/icon/Icon';
import { OverflowTooltip } from '../../shared/tooltip';

interface DeleteConfirmationModalProps {
    featurePermissions: number[];
}

const TITLE = 30;
const DESCRIPTION = 85;
const LABEL = 30;
const ACCESS_GROUPS = 80;

export const FeaturePermissionDisplay: React.FC<DeleteConfirmationModalProps> = ({ featurePermissions }) => {
    const availableFeaturePermissions = useAppSelector(getAvailableFeaturePermissions);

    const getIconToUse = useCallback((featurePermissionId: number) => {
        switch (featurePermissionId) {
            case FeaturePermission.DOWNLOAD_DOCUMENTS:
            case FeaturePermission.DOCUMENT_GROUP_MANAGEMENT:
            case FeaturePermission.DELETE_DOCUMENTS:
            case FeaturePermission.EDIT_DOCUMENT_DATA:
            case FeaturePermission.EDIT_DOCUMENT_REPORT_TEMPLATES:
            case FeaturePermission.COMPLETE_DOCUMENT_DETAILS:
            case FeaturePermission.UPLOAD_DOCUMENTS:
            case FeaturePermission.GENERATE_DOCUMENT_REPORTS:
                return Document;
            case FeaturePermission.CUSTOM_QUERYING_DOCUMENTS_WITH_AI:
            case FeaturePermission.CONFIGURE_CALCULATOR_PROMPTS:
                return Robot;
            case FeaturePermission.PLAYBOOK_MANAGEMENT:
            case FeaturePermission.QUERY_PLAYBOOKS:
            case FeaturePermission.SUGGEST_CHANGES_PLAYBOOKS:
                return PlaybookIcon;
            case FeaturePermission.SIGN_OFF_OPINIONS:
            case FeaturePermission.ADD_OPINION_NOTES:
                return Tick;
            case FeaturePermission.EDIT_OPINION_DATA:
            case FeaturePermission.DELETE_OPINIONS:
            case FeaturePermission.DOWNLOAD_OPINIONS:
            case FeaturePermission.DOWNLOAD_OPINION_SUMMARIES:
            case FeaturePermission.EDIT_OPINION_REPORT_TEMPLATES:
            case FeaturePermission.ADD_CLAUSES:
            case FeaturePermission.GENERATE_OPINION_REPORTS:
            case FeaturePermission.UPLOAD_OPINIONS:
                return Opinion;
            case FeaturePermission.SYSTEM_CLAUSE_MANAGEMENT:
            case FeaturePermission.MANAGE_INDUSTRY_STANDARD_OPINIONS:
                return Globe;
            case FeaturePermission.ENTITY_MANAGEMENT:
            case FeaturePermission.SUB_COUNTERPARTY_TYPE:
                return Agency;
            case FeaturePermission.RECEIVE_INDUSTRY_STANDARD_OPINION_UPDATES:
                return Mail;
            default:
                return User;
        }
    }, []);

    const featurePermissionsToDisplay = useMemo(() => featurePermissions.map(eachPermission => {
        const foundPermission = availableFeaturePermissions.find(({ featurePermissionId }) => featurePermissionId === eachPermission);
        const permissionName = foundPermission?.label || '';
        return { permissionName, icon: getIconToUse(eachPermission) };
    }).filter(({ permissionName }) => permissionName !== ''), [availableFeaturePermissions, featurePermissions, getIconToUse]);

    const scrollableHeight = useMemo(() => `calc(100% - ${TITLE + DESCRIPTION + LABEL + ACCESS_GROUPS}px)`, []);

    return (
        <Scrollable height={scrollableHeight}>
            <div className={styles.featurePermissionWrapper}>
                {featurePermissionsToDisplay.map(({ permissionName, icon }) => (
                    <div className={styles.featurePermissionBox} key={permissionName}>
                        <div className={styles.featurePermissionTitle} ><OverflowTooltip overlayText={permissionName} /></div>
                        <div className={styles.featurePermissionIcon} >
                            <Icon icon={icon} />
                        </div>
                    </div>
                ))}
            </div>
        </Scrollable>
    );
};
